import {
  IonButton,
  IonCol,
  IonContent,
  IonGrid,
  IonInput,
  IonLoading,
  IonPage,
  IonRow,
  IonSpinner,
} from "@ionic/react";
import { useState } from "react";
import "./Login.scss";
import appLogo from "../assets/images/Logo_SCW.png";
import clewerLogo from "../assets/images/Logo_Clewer_Black.png";
import { APIService } from "../network/APIService";
import { URL_IOS_APP, URL_ANDROID_APP } from "../utils/Constants";
import { RouteComponentProps } from "react-router";
import ErrorSuccessBox from "../components/ErrorSuccessBox";
import { TextFieldTypes } from "@ionic/core";

const Login: React.FC<RouteComponentProps> = ({ history }) => {
  // login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordInputType, setpasswordInputType] = useState<TextFieldTypes>("password");
  const [loginFailed, setLoginFailed] = useState(false);
  const [isVerifyingUser, setIsVerifyingUser] = useState(false);

  // password reset state
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [showErrorSuccessBoxForReset, setShowErrorSuccessBoxForReset] = useState<{
    show: boolean;
    success: boolean;
    message: string;
  }>({
    show: false,
    success: false,
    message: "",
  });

  const handleLoginClick = async () => {
    setLoginFailed(false);
    setIsVerifyingUser(true);

    // login to firebase
    const user = await APIService.login(email, password);
    if (user) {
      // log in successful, verify with Aimer server
      const verifyResponse = await APIService.verifyUser(user.uid, user.email!);
      verifyResponse ? history.push("/") : setLoginFailed(true);
    } else {
      setLoginFailed(true);
    }

    setIsVerifyingUser(false);
  };

  const handlePasswordReset = async () => {
    setShowErrorSuccessBoxForReset({
      ...showErrorSuccessBoxForReset,
      show: false,
    });
    setSendingRequest(true);
    const success = await APIService.requestPasswordReset(email);
    setShowErrorSuccessBoxForReset({
      show: true,
      success: success,
      message: success ? "Password reset email was sent." : "Request failed. Please try another email address.",
    });
    setSendingRequest(false);
  };

  return (
    <IonPage>
      <IonContent>
        <IonGrid id="loginGrid">
          <IonRow id="loginRow">
            <IonCol id="loginMainContainer" size="12" size-md="6" offset-md="3">
              <IonRow className="ion-align-items-stretch">
                <IonCol id="loginInnerContainer" size="11" offset="0.5">
                  <div>
                    <img id="loginImg" src={appLogo} alt="logo" width="193px" height="120px" className="displayBlock" />
                    {showPasswordReset ? (
                      // password reset layout
                      <>
                        <p id="passwordResetPrompt">
                          Let's get you into your account. Enter your email below and you’ll receive an email with a
                          link to reset your password.
                        </p>
                        <p style={{ marginTop: "8px" }} className="loginInputLabel">
                          Email address
                        </p>
                        <IonInput value={email} onIonChange={e => setEmail(e.detail.value!)} type="email" />
                        {showErrorSuccessBoxForReset.show && (
                          <ErrorSuccessBox
                            success={showErrorSuccessBoxForReset.success}
                            message={showErrorSuccessBoxForReset.message}
                          />
                        )}
                        <div id="loginButtonContainer">
                          <IonButton disabled={sendingRequest} className="yellowBtn" onClick={handlePasswordReset}>
                            {sendingRequest ? <IonSpinner className="resetPassSpinner" /> : <p>Request reset</p>}
                          </IonButton>
                          <IonButton
                            id="backToLoginBtn"
                            className="textOnlyBtn"
                            onClick={() => {
                              setShowErrorSuccessBoxForReset({
                                ...showErrorSuccessBoxForReset,
                                show: false,
                              });
                              setShowPasswordReset(false);
                            }}
                          >
                            Back to log in
                          </IonButton>
                        </div>
                      </>
                    ) : (
                      // log in layout
                      <>
                        <p className="loginInputLabel">Email address</p>
                        <IonInput value={email} onIonChange={e => setEmail(e.detail.value!)} type="email" />
                        <p className="loginInputLabel">Password</p>
                        <div className="passwordInputContainer">
                          <IonInput
                            value={password}
                            onIonChange={e => setPassword(e.detail.value!)}
                            type={passwordInputType}
                          />
                          <IonButton
                            style={{ width: "60px" }}
                            onClick={() =>
                              setpasswordInputType(prev => {
                                return prev === "text" ? "password" : "text";
                              })
                            }
                          >
                            {passwordInputType === "password" ? "show" : "hide"}
                          </IonButton>
                        </div>
                        {loginFailed && (
                          <ErrorSuccessBox
                            success={false}
                            message="Login failed. Please check email address and password."
                          />
                        )}
                        <div id="loginButtonContainer">
                            <IonButton
                              type="button"
                            disabled={(email.trim() && password.trim()) === ""}
                            className="yellowBtn"
                            onClick={handleLoginClick}
                          >
                            Log in
                          </IonButton>
                          <IonButton
                            id="forgotPasswordBtn"
                            className="textOnlyBtn"
                            onClick={() => {
                              setLoginFailed(false);
                              setShowPasswordReset(true);
                            }}
                          >
                            Forgotten password?
                          </IonButton>
                        </div>
                      </>
                    )}
                  </div>
                  <div id="loginLinksContainer">
                    <span id="loginGetAppPrompt">Get the app here</span>
                    <div>
                      <span>
                        <a href={`${URL_IOS_APP}`} target="_blank">
                          iOS app
                        </a>
                      </span>
                      <span>
                        <a href={`${URL_ANDROID_APP}`} target="_blank">
                          Android app
                        </a>
                      </span>
                    </div>
                    <img src={clewerLogo} alt="clewer logo" width="172px" height="80px" />
                  </div>
                </IonCol>
              </IonRow>
            </IonCol>
          </IonRow>
        </IonGrid>
        <IonLoading isOpen={isVerifyingUser} message={"Logging In..."} />
      </IonContent>
    </IonPage>
  );
};

export default Login;
